import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Stack,
  Divider,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  DateRange as DateRangeIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  Timeline as TimelineIcon,
  TableChart as TableChartIcon,
  PictureAsPdf as PdfIcon,
  TableView as ExcelIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import trLocale from 'date-fns/locale/tr';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { adminService } from '../../services/api';
import { useTheme, useMediaQuery } from '@mui/material';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { format, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';
import DejaVuSans from '../../assets/fonts/DejaVuSans-normal.js';

// jsPDF'ye fontu tanıt
jsPDF.API.events.push(['addFonts', function () {
  this.addFileToVFS('DejaVuSans.ttf', DejaVuSans);
  this.addFont('DejaVuSans.ttf', 'DejaVuSans', 'normal');
}]);

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const Reports = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportType, setReportType] = useState('appointments');
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1); // Ayın başlangıcı
    return date;
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date;
  });
  const [reportData, setReportData] = useState(null);
  const [viewMode, setViewMode] = useState('chart'); // 'chart' veya 'table'
  const [anchorEl, setAnchorEl] = useState(null);

  const formatDate = (dateString) => {
    try {
      const date = parseISO(dateString);
      return format(date, 'dd.MM.yyyy HH:mm', { locale: tr });
    } catch (error) {
      console.error('Tarih formatı hatası:', error);
      return 'Geçersiz Tarih';
    }
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      const filters = {
        type: reportType,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString()
      };
      const response = await adminService.getReports(filters);

      let formattedData = {};

      switch (reportType) {
        case 'appointments':
          formattedData = {
            appointments: response.data.data.appointments.map(appointment => ({
              id: appointment.id,
              date: formatDate(appointment.date),
              customerName: appointment.customer?.name || 'Bilinmiyor',
              customerPhone: appointment.customer?.phone || 'Bilinmiyor',
              mechanicName: appointment.mechanic?.name || 'Bilinmiyor',
              vehicleInfo: appointment.vehicle ? `${appointment.vehicle.brand} ${appointment.vehicle.model}` : 'Bilinmiyor',
              licensePlate: appointment.vehicle?.licensePlate || 'Bilinmiyor',
              services: appointment.appointmentServices?.map(as => ({
                name: as.Service?.name || 'Bilinmiyor',
                price: parseFloat(as.price) || 0
              })) || [],
              status: appointment.status,
              totalAmount: parseFloat(appointment.totalAmount) || 0,
              trackingNumber: appointment.trackingNumber
            }))
          };
          break;

        case 'revenue':
          formattedData = {
            dailyRevenue: response.data.data.dailyRevenue.map(item => ({
              date: formatDate(item.date),
              revenue: parseFloat(item.totalRevenue) || 0,
              appointments: parseInt(item.appointmentCount) || 0
            })),
            summary: {
              totalRevenue: parseFloat(response.data.data.totalRevenue) || 0,
              totalAppointments: parseInt(response.data.data.totalAppointments) || 0,
              averageRevenue: parseFloat(response.data.data.averageRevenue) || 0
            }
          };
          break;

        case 'services':
          formattedData = {
            services: response.data.data.map(service => ({
              name: service.name,
              revenue: parseFloat(service.totalRevenue) || 0,
              count: parseInt(service.totalCount) || 0
            }))
          };
          break;

        case 'mechanics':
          formattedData = {
            mechanics: response.data.data.map(mechanic => ({
              name: mechanic.name,
              completedJobs: parseInt(mechanic.totalAppointments) || 0,
              totalRevenue: parseFloat(mechanic.totalRevenue) || 0,
              averageRating: parseFloat(mechanic.averageRevenue) || 0
            }))
          };
          break;

        default:
          formattedData = response.data.data;
      }

      setReportData(formattedData);
      setError(null);
    } catch (err) {
      setError('Rapor yüklenirken bir hata oluştu.');
      console.error('Rapor hatası:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchReport();
    }
  }, [reportType, startDate, endDate]);

  const handleDownload = (format) => {
    if (!reportData) return;

    let data = [];
    let fileName = `${reportType}_report_${new Date().toISOString().split('T')[0]}`;

    switch (reportType) {
      case 'revenue':
        data = reportData.dailyRevenue.map(item => ({
          Tarih: item.date,
          'Toplam Gelir': `${item.revenue} TL`,
          'Randevu Sayısı': item.appointments,
          'Ortalama Gelir': `${(item.revenue / item.appointments).toFixed(2)} TL`
        }));
        break;

      case 'services':
        data = reportData.services.map(service => ({
          'Servis Adı': service.name,
          'Toplam İşlem': service.count,
          'Toplam Gelir': `${service.revenue} TL`,
          'Ortalama Gelir': `${(service.revenue / service.count).toFixed(2)} TL`
        }));
        break;

      case 'mechanics':
        data = reportData.mechanics.map(mechanic => ({
          'Usta Adı': mechanic.name,
          'Tamamlanan İşler': mechanic.completedJobs,
          'Toplam Gelir': `${mechanic.totalRevenue} TL`,
          'Ortalama Gelir': `${mechanic.averageRating} TL`
        }));
        break;

      case 'appointments':
        data = reportData.appointments.map(appointment => ({
          'Tarih': appointment.date,
          'Müşteri': appointment.customerName,
          'Telefon': appointment.customerPhone,
          'Araç': appointment.vehicleInfo,
          'Plaka': appointment.licensePlate,
          'Servisler': appointment.services.map(s => `${s.name} - ${s.price} TL`).join(', '),
          'Usta': appointment.mechanicName,
          'Durum': appointment.status,
          'Tutar': `${appointment.totalAmount} TL`
        }));
        break;
    }

    if (format === 'excel') {
      exportToExcel(data, fileName);
    } else if (format === 'pdf') {
      exportToPdf(data, fileName);
    }

    setAnchorEl(null);
  };

  const exportToExcel = (data, fileName) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Rapor');
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const exportToPdf = (data, fileName) => {
    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation
    doc.setFont('DejaVuSans'); // Türkçe karakter desteği
    // Title
    doc.setFontSize(16);
    doc.text('Rapor', 14, 15);
    // Report type
    doc.setFontSize(12);
    const reportTypeText = {
      'appointments': 'Randevu Raporu',
      'revenue': 'Gelir Raporu',
      'mechanics': 'Usta Performans Raporu',
      'services': 'Servis Raporu'
    }[reportType];
    doc.text(reportTypeText, 14, 25);
    // Date range
    doc.setFontSize(10);
    const dateRange = `${format(startDate, 'dd.MM.yyyy', { locale: tr })} - ${format(endDate, 'dd.MM.yyyy', { locale: tr })}`;
    doc.text(`Tarih Aralığı: ${dateRange}`, 14, 35);
    // Table headers
    const headers = Object.keys(data[0]).map(header => {
      const headerMap = {
        'Tarih': 'Tarih',
        'Müşteri': 'Müşteri',
        'Telefon': 'Telefon',
        'Araç': 'Araç',
        'Plaka': 'Plaka',
        'Servisler': 'Servisler',
        'Usta': 'Usta',
        'Durum': 'Durum',
        'Tutar': 'Tutar'
      };
      return headerMap[header] || header;
    });
    // Format table data
    const rows = data.map(item => {
      return Object.values(item).map(value => {
        if (typeof value === 'string' && value.includes('TL')) {
          return value;
        }
        if (typeof value === 'number') {
          return `${value.toLocaleString('tr-TR')} TL`;
        }
        return value;
      });
    });
    // Table settings
    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: 45,
      theme: 'grid',
      styles: {
        font: 'DejaVuSans', // Türkçe karakter desteği
        fontSize: 8,
        cellPadding: 2,
        overflow: 'linebreak',
        halign: 'left'
      },
      headStyles: {
        fillColor: [25, 118, 210],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center',
        font: 'DejaVuSans'
      },
      // Sütun genişliklerini küçültüp tabloyu otomatik sığdır
      columnStyles: {
        0: { cellWidth: 25 }, // Tarih
        1: { cellWidth: 30 }, // Müşteri
        2: { cellWidth: 25 }, // Telefon
        3: { cellWidth: 30 }, // Araç
        4: { cellWidth: 20 }, // Plaka
        5: { cellWidth: 45 }, // Servisler
        6: { cellWidth: 25 }, // Usta
        7: { cellWidth: 20 }, // Durum
        8: { cellWidth: 20 }  // Tutar
      },
      tableWidth: 'auto', // tabloyu sayfaya sığdır
      margin: { top: 45, left: 10, right: 10 }, // kenar boşluklarını azalt
      didDrawPage: function (data) {
        // Add page number
        doc.setFontSize(8);
        doc.text(
          `Sayfa ${data.pageCount}`,
          doc.internal.pageSize.width - 20,
          doc.internal.pageSize.height - 10,
          { align: 'right' }
        );
      }
    });
    doc.save(`${fileName}.pdf`);
  };

  const handleExportClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setAnchorEl(null);
  };

  const renderChart = () => {
    if (!reportData) return null;

    switch (reportType) {
      case 'revenue':
        if (!reportData.dailyRevenue || !Array.isArray(reportData.dailyRevenue)) {
          return (
            <Box display="flex" justifyContent="center" alignItems="center" height={400}>
              <Typography color="text.secondary">Gelir verisi bulunamadı</Typography>
            </Box>
          );
        }

        return (
          <Box>
            {/* Özet Bilgiler */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Toplam Gelir
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {reportData.summary.totalRevenue.toLocaleString('tr-TR')} TL
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Toplam Randevu
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {reportData.summary.totalAppointments}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Ortalama Gelir
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {reportData.summary.averageRevenue.toLocaleString('tr-TR')} TL
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Gelir Grafiği */}
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={reportData.dailyRevenue}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
                <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                <RechartsTooltip />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="revenue" stroke="#8884d8" name="Gelir (TL)" />
                <Line yAxisId="right" type="monotone" dataKey="appointments" stroke="#82ca9d" name="Randevu Sayısı" />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        );

      case 'services':
        if (!reportData.services || !Array.isArray(reportData.services)) {
          return (
            <Box display="flex" justifyContent="center" alignItems="center" height={400}>
              <Typography color="text.secondary">Servis verisi bulunamadı</Typography>
            </Box>
          );
        }
        return (
          <ResponsiveContainer width="100%" height={isMobile ? 300 : 400}>
            <PieChart>
              <Pie
                data={reportData.services}
                dataKey="revenue"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={isMobile ? 80 : 150}
              >
                {reportData.services.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <RechartsTooltip formatter={(value) => `${value} TL`} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'mechanics':
        if (!reportData.mechanics || !Array.isArray(reportData.mechanics)) {
          return (
            <Box display="flex" justifyContent="center" alignItems="center" height={400}>
              <Typography color="text.secondary">Usta verisi bulunamadı</Typography>
            </Box>
          );
        }
        return (
          <ResponsiveContainer width="100%" height={isMobile ? 300 : 400}>
            <BarChart data={reportData.mechanics}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
              <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
              <RechartsTooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="completedJobs" fill="#8884d8" name="Tamamlanan İşler" />
              <Bar yAxisId="right" dataKey="totalRevenue" fill="#82ca9d" name="Toplam Gelir (TL)" />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'appointments':
        if (!reportData.appointments || !Array.isArray(reportData.appointments)) {
          return (
            <Box display="flex" justifyContent="center" alignItems="center" height={400}>
              <Typography color="text.secondary">Randevu verisi bulunamadı</Typography>
            </Box>
          );
        }

        // Randevu durumlarına göre veri hazırlama
        const statusData = reportData.appointments.reduce((acc, appointment) => {
          const status = appointment.status;
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {});

        const pieData = Object.entries(statusData).map(([status, count]) => ({
          name: status === 'completed' ? 'Tamamlandı' :
            status === 'cancelled' ? 'İptal Edildi' :
              status === 'confirmed' ? 'Onaylandı' :
                status === 'pending' ? 'Beklemede' : 'İşlemde',
          value: count
        }));

        return (
          <ResponsiveContainer width="100%" height={isMobile ? 300 : 400}>
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={isMobile ? 80 : 150}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <RechartsTooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );

      default:
        return (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Typography color="text.secondary">Grafik verisi bulunamadı</Typography>
          </Box>
        );
    }
  };

  const renderTable = () => {
    if (!reportData) return null;

    // Mobilde tabloyu kart/kutu olarak göster
    if (isMobile) {
      switch (reportType) {
        case 'services':
          if (!reportData.services || !Array.isArray(reportData.services)) {
            return (
              <Alert severity="info" sx={{ my: 4, fontWeight: 500, fontSize: 15 }}>
                Servis verisi bulunamadı
              </Alert>
            );
          }
          return (
            <Box>
              {reportData.services.map((service, index) => (
                <Paper key={index} sx={{ mb: 2, p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600}>{service.name}</Typography>
                  <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                    <Chip label={`Toplam İşlem: ${service.count}`} size="small" />
                    <Chip label={`Toplam Gelir: ${service.revenue.toLocaleString('tr-TR')} TL`} size="small" />
                    <Chip label={`Ortalama Gelir: ${service.count > 0 ? (service.revenue / service.count).toLocaleString('tr-TR', { maximumFractionDigits: 2 }) : 0} TL`} size="small" />
                  </Box>
                </Paper>
              ))}
            </Box>
          );
        case 'appointments':
          if (!reportData.appointments || !Array.isArray(reportData.appointments)) {
            return (
              <Alert severity="info" sx={{ my: 4, fontWeight: 500, fontSize: 15 }}>
                Randevu verisi bulunamadı
              </Alert>
            );
          }
          return (
            <Box>
              {reportData.appointments.map((appointment) => (
                <Paper key={appointment.id} sx={{ mb: 2, p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600}>{appointment.customerName}</Typography>
                  <Typography variant="body2" color="text.secondary">{appointment.date}</Typography>
                  <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                    <Chip label={appointment.vehicleInfo} size="small" />
                    <Chip label={appointment.licensePlate} size="small" />
                    <Chip label={appointment.mechanicName} size="small" />
                  </Box>
                  <Box mt={1}>
                    <Typography variant="body2" color="text.secondary">Servisler:</Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {appointment.services.map(service => (
                        <Chip key={service.name} label={`${service.name} - ${service.price.toLocaleString('tr-TR')} TL`} size="small" />
                      ))}
                    </Box>
                  </Box>
                  <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                    <Chip label={appointment.status === 'completed' ? 'Tamamlandı' : appointment.status === 'cancelled' ? 'İptal Edildi' : appointment.status === 'confirmed' ? 'Onaylandı' : appointment.status === 'pending' ? 'Beklemede' : 'İşlemde'} color={appointment.status === 'completed' ? 'success' : appointment.status === 'cancelled' ? 'error' : appointment.status === 'confirmed' ? 'primary' : appointment.status === 'pending' ? 'warning' : 'info'} size="small" />
                    <Chip label={`Tutar: ${appointment.totalAmount.toLocaleString('tr-TR')} TL`} size="small" />
                  </Box>
                </Paper>
              ))}
            </Box>
          );
        case 'revenue':
          if (!reportData.dailyRevenue || !Array.isArray(reportData.dailyRevenue)) {
            return (
              <Alert severity="info" sx={{ my: 4, fontWeight: 500, fontSize: 15 }}>
                Gelir verisi bulunamadı
              </Alert>
            );
          }
          return (
            <Box>
              {reportData.dailyRevenue.map((item, index) => (
                <Paper key={index} sx={{ mb: 2, p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600}>{item.date}</Typography>
                  <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                    <Chip label={`Toplam Gelir: ${item.revenue} TL`} size="small" />
                    <Chip label={`Randevu Sayısı: ${item.appointments}`} size="small" />
                    <Chip label={`Ortalama Gelir: ${(item.revenue / item.appointments).toFixed(2)} TL`} size="small" />
                  </Box>
                </Paper>
              ))}
            </Box>
          );
        case 'mechanics':
          if (!reportData.mechanics || !Array.isArray(reportData.mechanics)) {
            return (
              <Alert severity="info" sx={{ my: 4, fontWeight: 500, fontSize: 15 }}>
                Usta verisi bulunamadı
              </Alert>
            );
          }
          return (
            <Box>
              {reportData.mechanics.map((master, index) => (
                <Paper key={index} sx={{ mb: 2, p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600}>{master.name}</Typography>
                  <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                    <Chip label={`Tamamlanan İşler: ${master.completedJobs}`} size="small" />
                    <Chip label={`Toplam Gelir: ${master.totalRevenue} TL`} size="small" />
                    <Chip label={`Ortalama Puan: ${master.averageRating}`} size="small" />
                  </Box>
                </Paper>
              ))}
            </Box>
          );
        default:
          return (
            <Alert severity="info" sx={{ my: 4, fontWeight: 500, fontSize: 15 }}>
              Tablo verisi bulunamadı
            </Alert>
          );
      }
    }

    // Masaüstü klasik tablo görünümü
    switch (reportType) {
      case 'services':
        if (!reportData.services || !Array.isArray(reportData.services)) {
          return (
            <Box display="flex" justifyContent="center" alignItems="center" height={400}>
              <Typography color="text.secondary">Servis verisi bulunamadı</Typography>
            </Box>
          );
        }
        return (
          <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: isTablet ? 'auto' : 'visible' }}>
            <Table size={isTablet ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>Servis Adı</TableCell>
                  <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>Toplam İşlem</TableCell>
                  <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>Toplam Gelir</TableCell>
                  <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>Ortalama Gelir</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.services.map((service, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>{service.name}</TableCell>
                    <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>{service.count}</TableCell>
                    <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>{service.revenue.toLocaleString('tr-TR')} TL</TableCell>
                    <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>
                      {service.count > 0
                        ? (service.revenue / service.count).toLocaleString('tr-TR', { maximumFractionDigits: 2 })
                        : 0} TL
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );
      case 'appointments':
        if (!reportData.appointments || !Array.isArray(reportData.appointments)) {
          return (
            <Box display="flex" justifyContent="center" alignItems="center" height={400}>
              <Typography color="text.secondary">Randevu verisi bulunamadı</Typography>
            </Box>
          );
        }
        return (
          <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: isTablet ? 'auto' : 'visible' }}>
            <Table size={isTablet ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>Tarih</TableCell>
                  <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>Müşteri</TableCell>
                  <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>Telefon</TableCell>
                  <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>Araç</TableCell>
                  <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>Plaka</TableCell>
                  <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>Servisler</TableCell>
                  <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>Usta</TableCell>
                  <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>Durum</TableCell>
                  <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>Tutar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.appointments.map((appointment) => (
                  <TableRow key={appointment.id}>
                    <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>{appointment.date}</TableCell>
                    <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>{appointment.customerName}</TableCell>
                    <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>{appointment.customerPhone}</TableCell>
                    <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>{appointment.vehicleInfo}</TableCell>
                    <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>{appointment.licensePlate}</TableCell>
                    <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>
                      {appointment.services.map(service => (
                        <div key={service.name} style={{ fontSize: isTablet ? 12 : 14 }}>
                          {service.name} - {service.price.toLocaleString('tr-TR')} TL
                        </div>
                      ))}
                    </TableCell>
                    <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>{appointment.mechanicName}</TableCell>
                    <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>
                      <Chip
                        label={appointment.status === 'completed' ? 'Tamamlandı' : appointment.status === 'cancelled' ? 'İptal Edildi' : appointment.status === 'confirmed' ? 'Onaylandı' : appointment.status === 'pending' ? 'Beklemede' : 'İşlemde'}
                        color={appointment.status === 'completed' ? 'success' : appointment.status === 'cancelled' ? 'error' : appointment.status === 'confirmed' ? 'primary' : appointment.status === 'pending' ? 'warning' : 'info'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>{appointment.totalAmount.toLocaleString('tr-TR')} TL</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );
      case 'revenue':
        if (!reportData.dailyRevenue || !Array.isArray(reportData.dailyRevenue)) {
          return (
            <Box display="flex" justifyContent="center" alignItems="center" height={400}>
              <Typography color="text.secondary">Gelir verisi bulunamadı</Typography>
            </Box>
          );
        }
        return (
          <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: isTablet ? 'auto' : 'visible' }}>
            <Table size={isTablet ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>Tarih</TableCell>
                  <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>Toplam Gelir</TableCell>
                  <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>Randevu Sayısı</TableCell>
                  <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>Ortalama Gelir</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.dailyRevenue.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>{item.date}</TableCell>
                    <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>{item.revenue} TL</TableCell>
                    <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>{item.appointments}</TableCell>
                    <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>{(item.revenue / item.appointments).toFixed(2)} TL</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );
      case 'mechanics':
        if (!reportData.mechanics || !Array.isArray(reportData.mechanics)) {
          return (
            <Box display="flex" justifyContent="center" alignItems="center" height={400}>
              <Typography color="text.secondary">Usta verisi bulunamadı</Typography>
            </Box>
          );
        }
        return (
          <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: isTablet ? 'auto' : 'visible' }}>
            <Table size={isTablet ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>Usta</TableCell>
                  <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>Tamamlanan İşler</TableCell>
                  <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>Toplam Gelir</TableCell>
                  <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>Ortalama Puan</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.mechanics.map((master, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>{master.name}</TableCell>
                    <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>{master.completedJobs}</TableCell>
                    <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>{master.totalRevenue} TL</TableCell>
                    <TableCell sx={{ fontSize: isTablet ? 13 : 15, py: isTablet ? 1 : 2 }}>{master.averageRating}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );
      default:
        return (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Typography color="text.secondary">Tablo verisi bulunamadı</Typography>
          </Box>
        );
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Başlık ve aksiyonlar */}
      <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems={isMobile ? 'stretch' : 'center'} mb={3} gap={isMobile ? 2 : 0}>
        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" fontWeight={600}>
          Raporlar
        </Typography>
        <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={isMobile ? 1 : 0}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportClick}
            disabled={!reportData}
            size={isMobile ? 'large' : 'medium'}
            fullWidth={isMobile}
          >
            Dışa Aktar
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleExportClose}
          >
            <MenuItem onClick={() => handleDownload('excel')}>
              <ListItemIcon>
                <ExcelIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Excel (.xlsx)</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleDownload('pdf')}>
              <ListItemIcon>
                <PdfIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>PDF (.pdf)</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, fontWeight: 500, fontSize: isMobile ? 14 : 16 }}>
          {error}
        </Alert>
      )}

      {/* Filtre alanları */}
      <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: 3 }} direction={isMobile ? 'column' : 'row'}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size={isMobile ? 'large' : 'medium'}>
            <InputLabel>Rapor Türü</InputLabel>
            <Select
              value={reportType}
              label="Rapor Türü"
              onChange={(e) => setReportType(e.target.value)}
            >
              <MenuItem value="appointments">Randevular</MenuItem>
              <MenuItem value="revenue">Gelir Analizi</MenuItem>
              <MenuItem value="mechanics">Usta Performansı</MenuItem>
              <MenuItem value="services">Servis Analizi</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={trLocale}>
            <DatePicker
              label="Başlangıç Tarihi"
              value={startDate}
              onChange={setStartDate}
              renderInput={(params) => <TextField {...params} fullWidth size={isMobile ? 'large' : 'medium'} />}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={4}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={trLocale}>
            <DatePicker
              label="Bitiş Tarihi"
              value={endDate}
              onChange={setEndDate}
              renderInput={(params) => <TextField {...params} fullWidth size={isMobile ? 'large' : 'medium'} />}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>

      {/* Tablo/Grafik görünüm butonları */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={1}>
          <Tooltip title="Grafik Görünümü">
            <IconButton
              color={viewMode === 'chart' ? 'primary' : 'default'}
              onClick={() => setViewMode('chart')}
              size={isMobile ? 'large' : 'medium'}
            >
              <BarChartIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Tablo Görünümü">
            <IconButton
              color={viewMode === 'table' ? 'primary' : 'default'}
              onClick={() => setViewMode('table')}
              size={isMobile ? 'large' : 'medium'}
            >
              <TableChartIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        viewMode === 'chart' ? renderChart() :
        <Box sx={{ width: '100%', overflowX: isMobile ? 'auto' : 'visible' }}>
          {renderTable()}
        </Box>
      )}
    </Box>
  );
};

export default Reports; 